import { useNavigation } from '@react-navigation/native';
import React, { useState } from 'react';
import {
  NativeModules,
  TouchableOpacity,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import {
  Menu,
  MenuOptions,
  MenuTrigger,
  renderers,
} from 'react-native-popup-menu';
import { tw } from '../../utils/tailwind';
import { CloseSVG, MenuSVG } from '../../../assets/svg';
import { useAuthMutations } from '../../api/hooks/auth/use-auth-mutations';
import { useAuth } from '../../contexs/auth-context';
import { AppStackScreenProps } from '../../routes';
import { getHeaderBarColorTheme } from '../../utils/conditional-style';
import { HEADER_HEIGHT, navigateToAdmin, useT } from '../../utils/helper';
import { ModalPrintDaySummary } from '../modals/modal-print-day-summary';
import { MenuOption } from './menu-option';
import { Platform } from 'react-native';
import env from '../../env';
import { useTerminalSetting } from '../../contexs/terminal-setting-context';
import { useTenant } from '../../contexs/tenant-context';
import { useAlert } from '../../contexs/alert-context';

interface MenuHeaderBarProps {
  openingStateName: string;
}

export const MenuHeaderBar = (props: MenuHeaderBarProps) => {
  // Hooks
  const { performLogout } = useAuthMutations();
  const { height } = useWindowDimensions();
  const { store_app_type } = useTerminalSetting();
  const { tenant } = useTenant();
  const { setCurrentCashier, signOut } = useAuth();
  const alert = useAlert();
  const navigation =
    useNavigation<AppStackScreenProps<'VendorDashboard'>['navigation']>();
  const t = useT('navigation');

  // States
  const [isModalPrintDaySummaryVisible, setModalPrintDaySummaryVisible] =
    useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { NotAnimatedContextMenu } = renderers;
  const { WifiModule } = NativeModules;

  const isCashRegisterEnabled = tenant.plan.cash_register_enabled;

  const handleLogout = () => {
    alert.confirm(t('areYouSureWantToLogOut?')).then(confirmed => {
      if (confirmed) {
        performLogout.mutateAsync().then(signOut);
      }
    });
  };

  return (
    <Menu
      renderer={NotAnimatedContextMenu}
      style={tw`gap-x-3 items-center`}
      onOpen={() => {
        setIsOpen(true);
      }}
      onClose={() => {
        setIsOpen(false);
      }}>
      <ModalPrintDaySummary
        isVisible={isModalPrintDaySummaryVisible}
        onModalHide={() => setModalPrintDaySummaryVisible(false)}
      />

      {/* Right Button */}
      <MenuTrigger
        style={tw`flex-row md:py-3 md:pl-6 md:pr-4 md:gap-x-2 p-3 justify-center items-center rounded-lg bg-white`}
        customStyles={{ TriggerTouchableComponent: TouchableOpacity }}>
        <Text style={tw`md:flex hidden text-xl leading-none`}>Menu</Text>
        {isOpen ? (
          <CloseSVG
            stroke={getHeaderBarColorTheme(props.openingStateName)}
            style={tw`w-5 h-5`}
          />
        ) : (
          <MenuSVG
            stroke={getHeaderBarColorTheme(props.openingStateName)}
            style={tw`w-5 h-5`}
          />
        )}
      </MenuTrigger>

      {/* Menu */}
      <MenuOptions
        customStyles={{
          optionsContainer: tw.style(
            `md:w-[376px] md:p-6 md:rounded-lg md:h-auto mt-[${
              HEADER_HEIGHT - 10
            }px] p-4 bg-white w-full h-[${height - HEADER_HEIGHT}px]`,
          ),
          optionsWrapper: tw.style('gap-y-3 content-start h-full'),
          optionWrapper: tw.style(
            'justify-center items-center text-center py-5 px-2 w-full bg-gray-100 rounded-lg',
          ),
        }}>
        {env.APP_URL ? (
          <MenuOption
            text={t('administration')}
            onSelect={() => navigateToAdmin(store_app_type)}
          />
        ) : null}

        <MenuOption
          text={t('currentOrders')}
          onSelect={() => navigation.navigate('VendorDashboard')}
        />

        {isCashRegisterEnabled ? (
          <>
            <MenuOption
              text={t('orderHistory')}
              onSelect={() => navigation.navigate('Orders', {})}
            />

            <MenuOption
              text={t('paymentHistory')}
              onSelect={() => navigation.navigate('Invoices', {})}
            />

            <MenuOption
              text={t('customers')}
              onSelect={() => navigation.navigate('Customers')}
            />
          </>
        ) : null}

        <MenuOption
          text={t('products')}
          onSelect={() => navigation.navigate('Products')}
        />

        {isCashRegisterEnabled ? (
          <MenuOption
            text={t('cashbook')}
            onSelect={() => navigation.navigate('Cashbook')}
          />
        ) : null}

        {/* show cash point closing for tenant with no_receipts setting */}
        {isCashRegisterEnabled || tenant.vendor_cash_point_closing_enabled ? (
          <MenuOption
            text={t('cashPointClosings')}
            onSelect={() => navigation.navigate('CashPointClosings')}
          />
        ) : null}

        {tenant.tenant_report_setting?.day_summaries_enabled ? (
          <MenuOption
            text={t('printDaySummary')}
            onSelect={() => setModalPrintDaySummaryVisible(true)}
          />
        ) : null}

        {/* If cash register is enabled, don't show completed orders menu */}
        {/* since it will be shown on OrdersNavigation instead */}
        {isCashRegisterEnabled ? null : (
          <MenuOption
            text={t('completedOrders')}
            onSelect={() => navigation.navigate('DeliveredCarts')}
          />
        )}

        {Platform.OS !== 'web' ? (
          <MenuOption
            text={t('wifiSettings')}
            onSelect={() => WifiModule.openWifiSetting()}
          />
        ) : null}

        <MenuOption
          text={t('printers')}
          onSelect={() => navigation.navigate('PrinterList')}
        />

        {isCashRegisterEnabled ? (
          <MenuOption
            text={t('lock')}
            theme="danger"
            onSelect={() => setCurrentCashier(null)}
          />
        ) : null}

        <View
          style={tw`md:mt-5 md:relative md:bottom-0 absolute bottom-3 w-full`}>
          <MenuOption
            theme="danger"
            text={t('deRegister')}
            onSelect={() => handleLogout()}
          />
        </View>
      </MenuOptions>
    </Menu>
  );
};
